import { useNavigate, useParams } from 'react-router-dom';
import { erpCookies } from '../utils/erpCookies.js';

/**
 * Hook personalizado para navegación que incluye automáticamente el tenant en la URL
 * 
 * @returns {Function} navigate - Función de navegación que agrega el tenant automáticamente
 * 
 * @example
 * const navigate = useTenantNavigation();
 * navigate('/tablaVentas'); // → /:tenant/tablaVentas
 * navigate('/gestionGenerarVenta/123'); // → /:tenant/gestionGenerarVenta/123
 */
export const useTenantNavigation = () => {
  const navigate = useNavigate();
  const { tenant: tenantFromURL } = useParams();

  /**
   * Navega a una ruta agregando automáticamente el tenant
   * 
   * @param {string} path - Ruta a la que navegar (con o sin / inicial)
   * @param {object} options - Opciones de navegación (replace, state, etc.)
   */
  const tenantNavigate = (path, options = {}) => {
    // Obtener tenant: primero de URL, luego de cookie persistente
    const tenant = tenantFromURL || erpCookies.get('tenantURL') || 'default';
    
    // Si el path ya incluye el tenant, no agregarlo de nuevo
    if (path.startsWith(`/${tenant}/`) || path.startsWith(`${tenant}/`)) {
      navigate(path, options);
      return;
    }

    // Remover / inicial si existe para evitar doble /
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    
    // Construir la ruta con tenant
    const fullPath = `/${tenant}/${cleanPath}`;
    
    console.log(`🔀 [useTenantNavigation] Navegando: ${path} → ${fullPath}`);
    navigate(fullPath, options);
  };

  return tenantNavigate;
};

/**
 * Hook para obtener el tenant actual de la URL o cookie
 * 
 * @returns {string} tenant - El tenant actual
 */
export const useCurrentTenant = () => {
  const { tenant: tenantFromURL } = useParams();
  const tenantFromCookie = erpCookies.get('tenantURL');
  
  return tenantFromURL || tenantFromCookie || 'default';
};

/**
 * Hook para construir rutas con tenant
 * 
 * @returns {Function} buildPath - Función que construye rutas con tenant
 */
export const useTenantPath = () => {
  const tenant = useCurrentTenant();
  
  /**
   * Construye una ruta con el tenant actual
   * 
   * @param {string} path - Ruta a construir
   * @returns {string} - Ruta con tenant
   */
  const buildPath = (path) => {
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `/${tenant}/${cleanPath}`;
  };
  
  return buildPath;
};

export default useTenantNavigation;

